import AccessDenied from "@/components/custom-access-denied"
import { MENUS } from "@/constants/admin-paths"
import { defaultLocale } from "@/constants/locales"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { ArrowLeftOutlined, DeleteOutlined, RedoOutlined, SaveOutlined, UndoOutlined } from "@ant-design/icons"
import { App, Button, Card, Input, Select, Space, Spin, Tag, Tooltip } from "antd"
import Title from "antd/es/typography/Title"
import { useRouter, useSearchParams } from "next/navigation"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import MenuItemInspector from "../components/MenuItemInspector"
import MenuSourcePanel from "../components/MenuSourcePanel"
import MenuStructureTree from "../components/MenuStructureTree"
import { useHistoryState } from "../hooks/use-history-state"
import menuService from "../service/menuService"
import type { MenuNode, MenuSourceItem } from "../types/menu"
import {
  buildTree,
  countNodes,
  detachNode,
  dropNode,
  findDepth,
  findNode,
  flattenTree,
  indentNode,
  moveNode,
  nextNodeId,
  outdentNode,
  updateNode,
} from "../utils/menu-tree"

const LOCALE_OPTIONS = [
  { value: "vi", label: t("Tiếng Việt") },
  { value: "en", label: t("Tiếng Anh") },
  { value: "cs", label: t("Tiếng Séc") },
]

const VALID_LOCALES = LOCALE_OPTIONS.map(option => option.value)

/** Dấu vân tay của bản nháp — so với bản đã lưu để biết còn thay đổi hay không. */
const serialize = (menuName: string, menuLocale: string, menuNodes: MenuNode[]) =>
  JSON.stringify({ name: menuName.trim(), locale: menuLocale, items: flattenTree(menuNodes) })

/**
 * Trình sửa một menu (/admin/menus/edit?id= và /admin/menus/create).
 * Bố cục 3 cột: nguồn nội dung → cấu trúc dạng cây → chi tiết mục đang chọn.
 * Cấu trúc giữ trong bộ nhớ dưới dạng cây và chỉ làm phẳng lại khi lưu, nên
 * phân cấp là thao tác tường minh chứ không đoán theo khoảng cách kéo ngang.
 */
const MenuEditor: React.FC = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const menuId = searchParams.get("id")
  const isCreateMode = !menuId

  const { message, modal } = App.useApp()
  const { hasPermission } = useCheckPermission()

  const [name, setName] = useState("")
  const [locale, setLocale] = useState(defaultLocale)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [sources, setSources] = useState<MenuSourceItem[]>([])
  const [loadingSources, setLoadingSources] = useState(true)
  const [loadingMenu, setLoadingMenu] = useState(!isCreateMode)
  const [saving, setSaving] = useState(false)
  const [baseline, setBaseline] = useState("")

  const tree = useHistoryState<MenuNode[]>([])
  const { value: nodes, set: setNodes, reset: resetNodes, undo, redo, canUndo, canRedo } = tree

  const canRead = hasPermission("menu", "find")
  const canSave = isCreateMode ? hasPermission("menu", "create") : hasPermission("menu", "update")
  const canDelete = !isCreateMode && hasPermission("menu", "delete")

  const snapshot = useMemo(() => serialize(name, locale, nodes), [locale, name, nodes])
  const isDirty = baseline !== "" && snapshot !== baseline

  const selectedNode = useMemo(() => (selectedId === null ? null : findNode(nodes, selectedId)), [nodes, selectedId])
  const selectedDepth = useMemo(() => (selectedId === null ? 0 : findDepth(nodes, selectedId)), [nodes, selectedId])

  useEffect(() => {
    let cancelled = false

    const loadSources = async () => {
      setLoadingSources(true)
      try {
        const items = await menuService.fetchMenuSources()
        if (!cancelled) setSources(items)
      } finally {
        if (!cancelled) setLoadingSources(false)
      }
    }

    loadSources()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadMenu = async () => {
      if (!menuId) {
        setName("")
        setLocale(defaultLocale)
        resetNodes([])
        setBaseline(serialize("", defaultLocale, []))
        return
      }

      setLoadingMenu(true)
      try {
        const record = await menuService.getMenuPreferenceById(menuId)
        if (cancelled) return

        const attributes = record?.attributes
        if (!attributes) {
          message.error(t("Không tìm thấy menu này."))
          router.push(`/admin${MENUS.LIST.PATH}`)
          return
        }

        const loadedName = attributes.name || ""
        const loadedLocale = VALID_LOCALES.includes(attributes.locale) ? attributes.locale : defaultLocale
        const loadedTree = buildTree(attributes.items)

        setName(loadedName)
        setLocale(loadedLocale)
        resetNodes(loadedTree)
        setSelectedId(null)
        setBaseline(serialize(loadedName, loadedLocale, loadedTree))
      } catch {
        if (!cancelled) {
          message.error(t("Lỗi khi tải menu. Vui lòng thử lại."))
        }
      } finally {
        if (!cancelled) setLoadingMenu(false)
      }
    }

    loadMenu()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId])

  // Cảnh báo khi rời trang lúc còn thay đổi chưa lưu — màn cũ reload thẳng, mất sạch.
  useEffect(() => {
    if (!isDirty) return

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }

    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [isDirty])

  // Ctrl+Z / Ctrl+Shift+Z cho cấu trúc menu. Khi con trỏ đang trong ô nhập thì
  // nhường lại cho undo mặc định của trình duyệt.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "z") return

      const target = event.target as HTMLElement | null
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return
      }

      event.preventDefault()
      if (event.shiftKey) redo()
      else undo()
    }

    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [redo, undo])

  const handleAddItems = useCallback(
    (items: { name: string; slug: string; url: string }[]) => {
      setNodes(current => {
        let nextId = nextNodeId(current)
        const added: MenuNode[] = items.map(item => ({
          id: nextId++,
          name: item.name,
          slug: item.slug,
          url: item.url || item.slug || "#",
          children: [],
        }))
        return [...current, ...added]
      })
      message.success(
        t("Đã thêm {count} mục vào menu").replace("{count}", String(items.length)),
      )
    },
    [message, setNodes],
  )

  const handleRemove = useCallback(
    (id: number) => {
      setNodes(current => detachNode(current, id).tree)
      setSelectedId(current => (current === id ? null : current))
    },
    [setNodes],
  )

  const handleSave = async () => {
    const trimmedName = name.trim()

    if (!trimmedName) {
      message.error(t("Vui lòng nhập tên menu."))
      return
    }

    if (!VALID_LOCALES.includes(locale)) {
      message.error(t("Ngôn ngữ không hợp lệ."))
      return
    }

    const payload = { name: trimmedName, items: flattenTree(nodes), locale }
    const savedSnapshot = serialize(trimmedName, locale, nodes)

    setSaving(true)
    try {
      if (menuId) {
        await menuService.updateMenuPreference(menuId, payload)
        setBaseline(savedSnapshot)
        message.success(t("Đã lưu menu"))
      } else {
        const response = await menuService.createMenuPreference(payload)
        const createdId = response?.data?.id
        setBaseline(savedSnapshot)
        message.success(t("Đã tạo menu"))

        if (createdId) {
          router.replace(`/admin${MENUS.EDIT.PATH}?id=${createdId}`)
        } else {
          router.push(`/admin${MENUS.LIST.PATH}`)
        }
      }
    } catch (error: any) {
      message.error(error?.message || t("Lưu menu thất bại. Vui lòng thử lại."))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = () => {
    modal.confirm({
      title: t("Xác nhận xóa menu"),
      content: t("Menu sẽ bị gỡ khỏi mọi trang đang dùng nó. Bạn có chắc muốn xóa?"),
      okText: t("Xóa"),
      cancelText: t("Hủy"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await menuService.deleteMenuPreference(menuId as string)
          message.success(t("Xóa menu thành công"))
          setBaseline(snapshot)
          router.push(`/admin${MENUS.LIST.PATH}`)
        } catch {
          message.error(t("Xóa menu thất bại"))
        }
      },
    })
  }

  const handleBack = () => {
    if (!isDirty) {
      router.push(`/admin${MENUS.LIST.PATH}`)
      return
    }

    modal.confirm({
      title: t("Còn thay đổi chưa lưu"),
      content: t("Rời khỏi trang sẽ mất các thay đổi chưa lưu. Bạn vẫn muốn thoát?"),
      okText: t("Thoát"),
      cancelText: t("Ở lại"),
      onOk: () => router.push(`/admin${MENUS.LIST.PATH}`),
    })
  }

  if (!canRead) {
    return <AccessDenied />
  }

  const itemCount = countNodes(nodes)

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          {t("Danh sách menu")}
        </Button>
        <Title level={2} className="!m-0">
          {isCreateMode ? t("Thêm menu mới") : t("Sửa menu")}
        </Title>
        {isDirty && <Tag color="warning">{t("Chưa lưu")}</Tag>}

        <span className="flex-1" />

        <Space>
          <Tooltip title={t("Hoàn tác (Ctrl+Z)")}>
            <Button icon={<UndoOutlined />} disabled={!canUndo} onClick={undo} />
          </Tooltip>
          <Tooltip title={t("Làm lại")}>
            <Button icon={<RedoOutlined />} disabled={!canRedo} onClick={redo} />
          </Tooltip>
          {canDelete && (
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t("Xóa menu")}
            </Button>
          )}
          {canSave && (
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              {t("Lưu menu")}
            </Button>
          )}
        </Space>
      </div>

      <Spin spinning={loadingMenu}>
        <Card type="inner" className="mb-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="min-w-[280px] flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                {t("Tên menu")}
              </label>
              <Input
                value={name}
                disabled={!canSave}
                placeholder={t("Ví dụ: Menu chính")}
                onChange={event => setName(event.target.value)}
              />
            </div>
            <div className="w-[200px]">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                {t("Ngôn ngữ")}
              </label>
              <Select
                value={locale}
                className="w-full"
                options={LOCALE_OPTIONS}
                disabled={!isCreateMode || !canSave}
                onChange={setLocale}
              />
              {!isCreateMode && (
                <p className="text-xs text-gray-400 mt-1 mb-0">
                  {t("Ngôn ngữ chỉ đặt được khi tạo menu.")}
                </p>
              )}
            </div>
          </div>
        </Card>

        <div className="flex flex-col xl:flex-row gap-4 items-start">
          <Card
            type="inner"
            title={t("Thêm mục")}
            className="w-full xl:w-[320px] shrink-0"
            styles={{ body: { paddingTop: 16 } }}
          >
            <MenuSourcePanel
              sources={sources}
              loading={loadingSources}
              disabled={!canSave}
              menuLocale={locale}
              onAdd={handleAddItems}
            />
          </Card>

          <Card
            type="inner"
            className="w-full flex-1 min-w-0"
            title={
              <div className="flex items-center gap-2">
                <span>{t("Cấu trúc menu")}</span>
                <Tag className="!mr-0">{t("{count} mục").replace("{count}", String(itemCount))}</Tag>
              </div>
            }
            extra={
              <span className="text-xs text-gray-400">
                {t("Kéo để đổi thứ tự · Tab tăng cấp · Shift+Tab giảm cấp")}
              </span>
            }
          >
            <MenuStructureTree
              nodes={nodes}
              selectedId={selectedId}
              disabled={!canSave}
              onSelect={setSelectedId}
              onDrop={(dragId, dropId, mode) => setNodes(current => dropNode(current, dragId, dropId, mode))}
              onIndent={id => setNodes(current => indentNode(current, id))}
              onOutdent={id => setNodes(current => outdentNode(current, id))}
              onMove={(id, direction) => setNodes(current => moveNode(current, id, direction))}
              onRemove={handleRemove}
            />
          </Card>

          <Card type="inner" title={t("Chi tiết mục")} className="w-full xl:w-[300px] shrink-0">
            <MenuItemInspector
              node={selectedNode}
              depth={selectedDepth < 0 ? 0 : selectedDepth}
              disabled={!canSave}
              onChange={patch => selectedId !== null && setNodes(current => updateNode(current, selectedId, patch))}
              onIndent={() => selectedId !== null && setNodes(current => indentNode(current, selectedId))}
              onOutdent={() => selectedId !== null && setNodes(current => outdentNode(current, selectedId))}
              onRemove={() => selectedId !== null && handleRemove(selectedId)}
            />
          </Card>
        </div>
      </Spin>
    </div>
  )
}

export default MenuEditor
